import { useEffect, useState, type ReactElement } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { kpiApi, companyApi, type KpiData } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Card, PageHeader, euro } from '../ui';

const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];
const eur0 = (cents: number) => (cents / 100).toLocaleString('de-DE', { maximumFractionDigits: 0 }) + ' €';
// Tooltip-Formatter (recharts ValueType -> Euro-String).
const fmtEuro = (v: unknown): string => euro(Number(v) || 0);
const pieColor = (i: number) => PIE_COLORS[i % PIE_COLORS.length] ?? '#2563eb';

// Kleine KPI-Kachel.
function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: ReactElement | string | undefined; tone?: 'good' | 'bad' | undefined }): ReactElement {
  return (
    <Card className="!p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactElement }): ReactElement {
  return (
    <Card className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {children}
    </Card>
  );
}

export default function KPIs(): ReactElement {
  const { active } = useCompany();
  const toast = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<KpiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!active) return;
    companyApi.usage(active.id).then((u) => setLocked(!u.features.includes('kennzahlen'))).catch(() => undefined);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    kpiApi.get(active.id, year)
      .then(setData)
      .catch((e) => { if (!locked) toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, year]);

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  if (locked) {
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title="Kennzahlen & KPIs" subtitle="Analysiere Umsatz, Forderungen, Kunden und Rentabilität." />
        <Card className="text-center">
          <p className="text-gray-700 dark:text-gray-300">🔒 Kennzahlen & KPIs sind ab dem <strong>Professional</strong>-Tarif verfügbar.</p>
          <a href="/app/einstellungen/tarif" className="mt-2 inline-block font-medium text-blue-600 hover:underline">Tarif ansehen</a>
        </Card>
      </div>
    );
  }

  const aging = data ? [
    { name: 'Nicht fällig', value: data.aging.current },
    { name: '1–30 T', value: data.aging.d30 },
    { name: '31–60 T', value: data.aging.d60 },
    { name: '60+ T', value: data.aging.d90plus },
  ].filter((a) => a.value > 0) : [];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Kennzahlen & KPIs" subtitle="Analysiere Umsatz, Forderungen, Kunden und Rentabilität." />

      <div className="mb-4">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading || !data ? (
        <p className="text-gray-500">Lädt …</p>
      ) : (
        <div className="space-y-4">
          {/* KPI-Kacheln */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi
              label="Umsatz"
              value={eur0(data.umsatzCents)}
              sub={data.yoyProzent !== null ? (
                <span className={`inline-flex items-center gap-1 ${data.yoyProzent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.yoyProzent >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {data.yoyProzent >= 0 ? '+' : ''}{data.yoyProzent}% ggü. Vorjahr
                </span>
              ) : 'kein Vorjahr'}
            />
            <Kpi label="Gewinn (EÜR)" value={eur0(data.gewinnCents)} tone={data.gewinnCents >= 0 ? 'good' : 'bad'}
              sub={`Umsatzrendite ${data.umsatzrenditeProzent}%`} />
            <Kpi label="Offene Forderungen" value={eur0(data.offeneForderungenCents)}
              sub={`${data.offeneAnzahl} Rechnung${data.offeneAnzahl === 1 ? '' : 'en'}`} />
            <Kpi label="Ø Zahlungsdauer" value={data.avgPaymentDays !== null ? `${data.avgPaymentDays} Tage` : '—'}
              sub={data.onTimeQuote !== null ? `${data.onTimeQuote}% pünktlich bezahlt` : undefined} />
          </div>

          {/* Umsatz-Trend + Kostenquote/Liquiditaet */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ChartCard title="Umsatzverlauf (netto)">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.monthly} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `${Math.round(v / 100000)}k`} tick={{ fontSize: 12 }} width={36} />
                    <Tooltip formatter={fmtEuro} labelClassName="text-gray-900" />
                    <Line type="monotone" dataKey="incomeCents" name="Umsatz" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <div className="space-y-3">
              <Kpi label="Ausgaben" value={eur0(data.ausgabenCents)}
                sub={data.kostenquoteProzent !== null ? `Kostenquote ${data.kostenquoteProzent}%` : undefined} />
              {data.hatBank && <Kpi label="Liquidität (Bank)" value={eur0(data.liquiditaetCents)} tone={data.liquiditaetCents >= 0 ? 'good' : 'bad'} />}
            </div>
          </div>

          {/* Forderungs-Aging + Kostenstruktur */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Offene Forderungen nach Fälligkeit">
              {aging.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">Keine offenen Forderungen.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={aging} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                      {aging.map((_, i) => <Cell key={i} fill={i === 0 ? '#16a34a' : i === aging.length - 1 ? '#dc2626' : pieColor(i)} />)}
                    </Pie>
                    <Tooltip formatter={fmtEuro} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Kostenstruktur">
              {data.kostenGruppen.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">Keine Kosten erfasst.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={data.kostenGruppen} dataKey="cents" nameKey="label" cx="50%" cy="50%" outerRadius={90}>
                      {data.kostenGruppen.map((_, i) => <Cell key={i} fill={pieColor(i)} />)}
                    </Pie>
                    <Tooltip formatter={fmtEuro} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Top-Kunden */}
          <ChartCard title="Top-Kunden nach Umsatz">
            {data.topCustomers.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Noch keine bezahlten Rechnungen mit Kundenzuordnung.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, data.topCustomers.length * 38)}>
                <BarChart data={data.topCustomers} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => `${Math.round(v / 100000)}k`} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={fmtEuro} />
                  <Bar dataKey="netCents" name="Umsatz" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  );
}
